/**
 * Analytics tracking utility
 * Handles session management and event tracking
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = 'visaasure_session_id';

/**
 * Get or create session ID for anonymous tracking
 */
export const getSessionId = () => {
  let sessionId = localStorage.getItem(SESSION_KEY);
  
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem(SESSION_KEY, sessionId);
  }
  
  return sessionId;
};

/**
 * Track an analytics event
 * @param {Object} eventData
 * @param {string} eventData.component_type - Type of component (service_card, destination_card, blog_card, etc.)
 * @param {string} eventData.action_type - Type of action (view, click, hover, etc.)
 * @param {string} [eventData.item_id] - ID of the item being tracked
 * @param {string} [eventData.item_name] - Human-readable name of the item
 * @param {Object} [eventData.metadata] - Additional metadata
 */
export const trackEvent = async (eventData) => {
  try {
    const sessionId = getSessionId();
    
    const payload = {
      session_id: sessionId,
      component_type: eventData.component_type,
      action_type: eventData.action_type,
      item_id: eventData.item_id || null,
      item_name: eventData.item_name || null,
      metadata: {
        ...eventData.metadata,
        page_url: window.location.pathname,
        referrer: document.referrer || 'direct',
        timestamp_client: new Date().toISOString()
      }
    };
    
    // Send to backend (fire and forget - don't await)
    fetch(`${BACKEND_URL}/api/analytics/track`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }).catch(error => {
      // Silently fail - don't disrupt user experience
      console.debug('Analytics tracking failed:', error);
    });
    
  } catch (error) {
    console.debug('Analytics error:', error);
  }
};

/**
 * Track component view
 */
export const trackView = (componentType, itemId, itemName, metadata = {}) => {
  trackEvent({
    component_type: componentType,
    action_type: 'view',
    item_id: itemId,
    item_name: itemName,
    metadata
  });
};

/**
 * Track component click
 */
export const trackClick = (componentType, itemId, itemName, metadata = {}) => {
  trackEvent({
    component_type: componentType,
    action_type: 'click',
    item_id: itemId,
    item_name: itemName,
    metadata
  });
};

/**
 * Track hover with duration
 */
export const trackHover = (componentType, itemId, itemName, duration, metadata = {}) => {
  trackEvent({
    component_type: componentType,
    action_type: 'hover',
    item_id: itemId,
    item_name: itemName,
    metadata: {
      ...metadata,
      hover_duration_ms: duration
    }
  });
};

/**
 * Track carousel interaction
 */
export const trackCarousel = (componentType, itemId, itemName, direction, metadata = {}) => {
  trackEvent({
    component_type: componentType,
    action_type: 'carousel_interaction',
    item_id: itemId,
    item_name: itemName,
    metadata: {
      ...metadata,
      direction
    }
  });
};

/**
 * Track form submission
 */
export const trackFormSubmit = (formType, metadata = {}) => {
  trackEvent({
    component_type: 'form',
    action_type: 'submit',
    item_id: formType,
    item_name: formType,
    metadata
  });
};

/**
 * Track CTA button click
 */
export const trackCTA = (ctaId, ctaName, metadata = {}) => {
  trackEvent({
    component_type: 'cta_button',
    action_type: 'click',
    item_id: ctaId,
    item_name: ctaName,
    metadata
  });
};

/**
 * Component-specific tracking helpers
 */

// Service card tracking
export const trackServiceView = (serviceId, serviceName) => {
  trackView('service_card', serviceId, serviceName);
};

export const trackServiceClick = (serviceId, serviceName) => {
  trackClick('service_card', serviceId, serviceName);
};

// Destination card tracking
export const trackDestinationView = (countryName) => {
  trackView('destination_card', countryName, countryName);
};

export const trackDestinationClick = (countryName) => {
  trackClick('destination_card', countryName, countryName);
};

export const trackDestinationCarousel = (countryName, direction) => {
  trackCarousel('destination_card', countryName, countryName, direction);
};

// Blog tracking
export const trackBlogListView = (blogSlug, blogTitle) => {
  trackView('blog_card', blogSlug, blogTitle, { context: 'list' });
};

export const trackBlogClick = (blogSlug, blogTitle) => {
  trackClick('blog_card', blogSlug, blogTitle, { context: 'list' });
};

export const trackBlogDetailView = (blogSlug, blogTitle) => {
  trackView('blog_detail', blogSlug, blogTitle, { context: 'detail' });
};
